import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { InfoMessage, LoadingIndicator, PopOutChart } from 'sharedComponents';
import { chartPlugins } from './constants';
import useChartConfig from './useChartConfig';
import {
    EnableUniqueScaling,
    LastSyncDate,
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup
} from 'sharedComponents/Chart/headers';
import { useChartResize } from 'sharedHooks';


const TrendChart = ({
    selectedTrendKPIs,
    selectedTrendsData,
    removeSelectedKpiLegend,
    trendDataLoadingStatus,
    userLimits,
    enableUniqueScaling,
    availableTrendKPIs,
    lastMeasurementTimeStampLabel,
    isZoomed,
    chartKey = '',
    reloadAllSelectedTrendKpiData,
    setIsZoomed,
    zoomInToSelectedTrendKpiData,
    yAxisTitleVisible,
    mainChartHeader,
    multiChart,
    dateSelection,
    selectedLimits,
    trendComments,
    setShowCommentDrawer,
    setShowCommentModal,
    getTrendCommentsRequest,
    energyViewMode,
    isOnlyEnergySignalsSelected,
    selectedDataPointTimestamp,
    hasStackedAreaChartVisualization
}) => {
    const containerRef = useRef();
    const chartRef = useRef();
    const [chartInstance, setChartInstance] = useState(null);

    useChartResize(containerRef, chartRef);

    const resetServerZoom = () => {
        reloadAllSelectedTrendKpiData(selectedTrendKPIs);
        getTrendCommentsRequest?.();
    };
    const data = false;

    const zoomIn = (from, to) => {
        zoomInToSelectedTrendKpiData(availableTrendKPIs, selectedTrendKPIs, from, to);
        getTrendCommentsRequest?.(from, to);
    };

    const graphOptions = useChartConfig(
        selectedTrendsData,
        userLimits,
        yAxisTitleVisible,
        removeSelectedKpiLegend,
        multiChart,
        selectedLimits,
        availableTrendKPIs,
        energyViewMode,
        isOnlyEnergySignalsSelected,
        trendComments,
        setShowCommentDrawer,
        setShowCommentModal,
        chartInstance,
        selectedDataPointTimestamp,
        hasStackedAreaChartVisualization
    );

    const CustomHeader = withPopOutRow([
        withLeftButtonGroup([LastSyncDate, EnableUniqueScaling]),
        withRightButtonGroup([ResetZoomButton, PopOutButton])]);

    return (
        <React.Suspense fallback={<LoadingIndicator />}>
            <div ref={containerRef} className={`trend-chart-container ${selectedTrendKPIs?.length > 0 ? 'signal-selected' : ''}`}>
                <PopOutChart
                    chartRef={chartRef}
                    hasKpi={!!selectedTrendKPIs?.length}
                    data={data}
                    selectedTrendKPIs={selectedTrendKPIs}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.chartNoKPIsSelected'
                        text='ABB.Powertrain.Frontend.chartNoKPIsSelectedUserGuide' />}
                    hasData={!!selectedTrendsData?.length}
                    renderNoData={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.noDataLabelAvailable'
                        text='ABB.Powertrain.Frontend.adjustDateRangeOrChartType' />}
                    enableUniqueScaling={enableUniqueScaling}
                    lastMeasurementLabel={lastMeasurementTimeStampLabel}
                    zoomInServer={zoomIn}
                    resetServerZoom={resetServerZoom}
                    isZoomed={isZoomed}
                    setIsZoomed={setIsZoomed}
                    loadingStatus={trendDataLoadingStatus}
                    options={graphOptions}
                    modules={chartPlugins}
                    container={chartKey}
                    mainChartHeader={mainChartHeader || CustomHeader}
                    availableTrendKPIs={availableTrendKPIs || undefined}
                    dateSelection={dateSelection}
                    isTrendSignal={true}
                    onChartReady={setChartInstance}
                    chartInstance={chartInstance}
                />
            </div>
        </React.Suspense>
    );
};

TrendChart.propTypes = {
    reloadAllSelectedTrendKpiData: PropTypes.func,
    setIsZoomed: PropTypes.func,
    zoomInToSelectedTrendKpiData: PropTypes.func,
    selectedTrendKPIs: PropTypes.array,
    enableUniqueScaling: PropTypes.bool,
    lastMeasurementTimeStampLabel: PropTypes.string,
    selectedTrendsData: PropTypes.array,
    availableTrendKPIs: PropTypes.array,
    trendDataLoadingStatus: PropTypes.string,
    userLimits: PropTypes.array,
    isZoomed: PropTypes.bool,
    chartKey: PropTypes.string,
    yAxisTitleVisible: PropTypes.bool,
    removeSelectedKpiLegend: PropTypes.bool,
    mainChartHeader: PropTypes.element,
    multiChart: PropTypes.bool,
    dateSelection: PropTypes.object,
    selectedLimits: PropTypes.array,
    energyViewMode: PropTypes.string,
    isOnlyEnergySignalsSelected: PropTypes.bool,
    hasStackedAreaChartVisualization: PropTypes.bool,
    trendComments: PropTypes.array,
    setShowCommentDrawer: PropTypes.func,
    setShowCommentModal: PropTypes.func,
    getTrendCommentsRequest: PropTypes.func
};

export default TrendChart;
